/**
 * LoadingState — F-500 2026 unified shimmer.
 *
 * Was previously a spinning circle ("Ladekreis"); now renders the same
 * skeleton-shimmer language used by every `loading.tsx` and in-page
 * load-branch across the admin. One animation, one rhythm — no more
 * mix of spinners, pulses and gradient rings.
 *
 * The `message` prop is still accepted for API compat but is rendered
 * inline above the skeleton block so screen-readers + sighted users
 * keep the context-cue. Set `message` to an empty string to suppress.
 */

import { SkeletonBlock } from '@/components/admin/_shared';

interface LoadingStateProps {
  /** Optional context label rendered above the shimmer. */
  message?: string;
  /** Geometry scale — controls the inner block heights. */
  size?: 'sm' | 'md' | 'lg';
  /** Vertical padding inside the wrapper. */
  centered?: boolean;
}

export function LoadingState({
  message = 'Loading...',
  size = 'md',
  centered = true,
}: LoadingStateProps) {
  const padding = centered
    ? size === 'sm'
      ? 'py-4'
      : size === 'lg'
        ? 'py-16'
        : 'py-10'
    : '';

  const blockHeight =
    size === 'sm' ? 'h-3' : size === 'lg' ? 'h-6' : 'h-4';

  return (
    <div className={`flex flex-col items-center text-center ${padding}`}>
      {message ? (
        <p className="mb-3 text-sm text-gray-500" aria-live="polite">
          {message}
        </p>
      ) : null}
      <div className="flex w-full max-w-sm flex-col items-stretch gap-2">
        <SkeletonBlock className={`${blockHeight} w-full`} />
        <SkeletonBlock className={`${blockHeight} w-3/4`} />
      </div>
    </div>
  );
}
